import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { formatarCEP, formatarCPFCNPJ, formatarTelefone } from "../../../../../../../helpers/masks";
import { IDataDetalharCooperado } from "../../../../../../../types/api/cadastros/detalhar-cooperado";
import SearchIcon from '@mui/icons-material/Search';
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import { desformatarCEP, desformatarCPFCNPJ } from "../../../../../../../helpers/unmask";
import { ViaCEPResposta } from "../../../../../../../types/externas/viacep-resultado";
import { useMemo } from "react";
import PaperDraggable from "../../../../../../../components/PaperDraggable";
import { enqueueSnackbar } from "notistack";

export default function DialogEditarDadosCadastrais({
    open,
    onClose,
    data
}: DialogEditarDadosCadastraisProps) {

    const queryClient = useQueryClient();

    const ufs = useMemo(() => ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"], [])

    const methods = useForm<FormData>({
        defaultValues: {
            nome: data.nome_pessoa,
            email: data.email || "",
            telefone: data.telefone,
            documento_pessoa: formatarCPFCNPJ(data.documento_pessoa),
            cep: desformatarCEP(data.cep) || "",
            endereco: data.endereco || "",
            numero_endereco: `${data.numero_endereco}` || "",
            complemento_endereco: data.complemento_endereco || "",
            bairro: data.bairro || "",
            cidade: data.cidade?.nome || "",
            uf: data.cidade?.estado || ""
        }
    })
    const { control, watch, getValues, setValue, handleSubmit } = methods;

    //queries e mutations
    const { data: cidades } = useQuery({
        queryKey: ["pesquisar-cidades", watch(`uf`)],
        queryFn: async () => {
            if (watch(`uf`) === undefined) return []
            let result = await axiosClient.get("/pesquisar-cidades", {
                params: {
                    uf: getValues(`uf`)
                }
            })
            return result?.data
        }
    });
    const { dataCidades }: ICidades = {
        dataCidades: cidades || []
    }

    const { mutate: editar, isLoading } = useMutation({
        mutationKey: 'editar-cooperado',
        mutationFn: (inputs: EditarCooperado) => axiosClient.put(`/cadastros/editar-cooperado/${data.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao editar cooperado!', { variant: 'error' });
        },
        onSuccess: () => {
            enqueueSnackbar(`Cooperado editado com sucesso`, { variant: "success" });
            queryClient.invalidateQueries('cadastros/detalhar-cooperado')
            onClose()
        }
    })

    //

    //funções manipuladoras
    const handlePesquisarCep = async () => {
        const { cep } = getValues()
        if (cep === undefined || cep === "") return

        const unmaskCep = desformatarCEP(cep)

        try {
            const response = await axiosClient.get<ViaCEPResposta>(`https://viacep.com.br/ws/${unmaskCep}/json/`);
            const { bairro, logradouro, localidade, uf } = response.data
            setValue(`uf`, uf)
            setValue(`bairro`, bairro)
            setValue(`cidade`, localidade)
            setValue(`endereco`, logradouro)
        } catch (error) {
            console.error('Erro ao buscar endereço por CEP:', error)
            return
        }
    }

    const onSubmit = (data: FormData) => {
        const params: EditarCooperado = {
            campos: "Dados Cadastrais",
            nome: data.nome,
            email: data.email || null,
            telefone: data.telefone || null,
            documento_pessoa: desformatarCPFCNPJ(data.documento_pessoa),
            cep: data.cep,
            endereco: data.endereco,
            numero_endereco: data.numero_endereco,
            complemento_endereco: data.complemento_endereco || null,
            bairro: data.bairro,
            cidade: data.cidade,
            uf: data.uf || null
        }

        editar(params);
    }

    return (
        <FormProvider {...methods}>
            <Dialog
                maxWidth="md"
                fullWidth
                open={open}
                onClose={onClose}
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Editar Dados Cadastrais</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name="nome"
                                control={control}
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field, fieldState: { error, invalid } }) => {
                                    return (
                                        <TextField
                                            {...field}
                                            required
                                            error={invalid}
                                            label="Nome"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            slotProps={{ htmlInput: { maxLength: 40 } }}
                                            helperText={error ? error.message : ""}
                                        />
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field, fieldState: { error, invalid } }) => {
                                    return (
                                        <TextField
                                            {...field}
                                            error={invalid}
                                            label="Email"
                                            autoComplete="email"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            slotProps={{ htmlInput: { maxLength: 45 } }}
                                            helperText={error ? error.message : ""}
                                        />
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <Controller
                                name="documento_pessoa"
                                control={control}
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field, fieldState: { error, invalid } }) => {
                                    return (
                                        <TextField
                                            {...field}
                                            required
                                            error={invalid}
                                            label="CPF/CNPJ"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange(formatarCPFCNPJ(e.target.value))}
                                            slotProps={{ htmlInput: { maxLength: 17 } }}
                                            helperText={error ? error.message : ""}
                                        />
                                    );
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, md: 4 }}>
                            <Controller
                                name="telefone"
                                control={control}
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field, fieldState: { error, invalid } }) => {
                                    return (
                                        <TextField
                                            {...field}
                                            required
                                            error={invalid}
                                            label="Telefone"
                                            autoComplete="telefone"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange(formatarTelefone(e.target.value))}
                                            slotProps={{ htmlInput: { maxLength: 45 } }}
                                            helperText={error ? error.message : ""}
                                        />
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 4 }}>
                            <Controller
                                name={`cep`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="CEP"
                                        variant="outlined"
                                        defaultValue={""}
                                        fullWidth
                                        required
                                        onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                                        onBlur={handlePesquisarCep}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                        slotProps={{
                                            input: {
                                                endAdornment: <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handlePesquisarCep}
                                                        edge="end"
                                                    >
                                                        <SearchIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, md: 4 }}>
                            <Controller
                                name={`bairro`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="Bairro"
                                        defaultValue={""}
                                        variant="outlined"
                                        required
                                        fullWidth
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>


                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name={`uf`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <FormControl fullWidth {...field} error={invalid} required>
                                        <InputLabel id="uf-select">UF</InputLabel>
                                        <Select
                                            labelId="uf-select"
                                            value={field.value}
                                            label="UF"
                                            onChange={(e) => field.onChange(e.target.value)}
                                        >
                                            {ufs.map((uf, index) => (
                                                <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                            ))}
                                        </Select>
                                        {error && (
                                            <FormHelperText>{error?.message}</FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, md: 5 }}>
                            <Controller
                                name={`cidade`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <>
                                        <Autocomplete
                                            {...field}
                                            fullWidth
                                            disablePortal
                                            options={dataCidades}
                                            defaultValue={""}
                                            onChange={(_, v) => field.onChange(v)}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    label="Cidade"
                                                    required
                                                    defaultValue={""}
                                                    error={Boolean(invalid)}
                                                    helperText={error?.message || ""}
                                                />
                                            }
                                        />
                                    </>
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name={`endereco`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="Endereço"
                                        defaultValue={""}
                                        variant="outlined"
                                        required
                                        fullWidth
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name={`complemento_endereco`}
                                control={control}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="Complemento"
                                        defaultValue={""}
                                        variant="outlined"
                                        required
                                        fullWidth
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name={`numero_endereco`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="Número"
                                        defaultValue={""}
                                        variant="outlined"
                                        required
                                        fullWidth
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>

                    </Grid>

                </DialogContent>
                <DialogActions>
                    <Button disabled={isLoading} onClick={onClose}>Cancelar</Button>
                    <Button disabled={isLoading} onClick={handleSubmit(onSubmit)} color="primary" variant="contained">Editar</Button>
                </DialogActions>
            </Dialog>

        </FormProvider>
    )
}

interface DialogEditarDadosCadastraisProps {
    open: boolean;
    onClose: () => void;
    data: IDataDetalharCooperado
}

type FormData = {
    nome: string;
    email: string;
    telefone: string;
    documento_pessoa: string;
    cep: string;
    endereco: string;
    numero_endereco: string;
    complemento_endereco: string;
    bairro: string;
    cidade: string;
    uf: string;
}

type EditarCooperado = {
    campos: "Dados Cadastrais";
    nome: string;
    email: string | null;
    telefone: string | null;
    documento_pessoa: string;
    cep: string;
    endereco: string;
    numero_endereco: string;
    complemento_endereco: string | null;
    bairro: string;
    cidade: string;
    uf: string | null;
}

interface ICidades {
    dataCidades: Array<string>
}